import { Component, OnDestroy, OnInit, ViewChild, SecurityContext, Inject } from '@angular/core';
import { ReleaseDefinition } from '../models/release-definition';
import { AdoService } from '../services/ado.service';
import { RcjitService } from './rcjit.service';
import { SlackService } from '../services/slack.service';
import {
  AbstractControl,
    FormBuilder,
    FormGroup,
    ValidatorFn,
    Validators,
} from '@angular/forms';
import { AdoOrg } from '../models/ado-org';
import { Observable, Subject, Subscription, of, throwError } from 'rxjs';
import { catchError, map, startWith, takeUntil, tap } from 'rxjs/operators';
import { AdoUserProfile } from '../models/ado-user-profile';
import { AdoAccount } from '../models/ado-account';
import { AdoResponse } from '../models/ado-response';
import { EnvironmentService } from '../services/environment.service';
import { Environment } from '../models/environment';
import { ValidationResult, ValidationStatusEnum, ValidationResultType } from '../models/validation-result';
import { HttpErrorResponse, HttpResponse } from '@angular/common/http';
import { BreakpointObserver } from '@angular/cdk/layout';
import { MatStepper, StepperOrientation } from '@angular/material/stepper';
import { STEPPER_GLOBAL_OPTIONS } from '@angular/cdk/stepper';
import { MatDialog } from '@angular/material/dialog';
import { ErrorDialogComponent } from '../dialog/error-dialog/error-dialog.component';
import { ValidationEnvironment } from '../models/validation-environments';
import { ActivatedRoute } from '@angular/router';
import { ReleaseDefinitionChangesResult } from '../mario/models/release-definition-changes-results';
import { DomSanitizer, SafeHtml } from '@angular/platform-browser';
import { UserProfileService } from '../services/user-profile.service';
import { LoadingService } from '../services/loading.service';

@Component({
    selector: 'app-rcjit',
    templateUrl: './rcjit.component.html',
    styleUrls: ['./rcjit.component.css', '../home/home.component.css'],
    providers: [
        {
            provide: STEPPER_GLOBAL_OPTIONS,
            useValue: { displayDefaultIndicatorType: false }
        }
    ],
    standalone: false
})
export class RcjitComponent implements OnInit, OnDestroy {
  releaseDefinitions: Array<ReleaseDefinition>;
  filteredReleaseDefinitions: Observable<Array<ReleaseDefinition>>;
  firstFormGroup: FormGroup;
  secondFormGroup: FormGroup;
  thirdFormGroup: FormGroup;
  availableOrgs: Array<AdoOrg> = new Array<AdoOrg>();
  jitFinished: boolean = false;
  private userProfile!: AdoUserProfile;
  availableAdoAccounts: Array<AdoAccount> = new Array<AdoAccount>();
  isAuthenticated: boolean = false;
  isPlatform: boolean = false;
  defaultOrg?: AdoAccount;
  defaultProject?: string;
  private readonly _destroying$ = new Subject<void>();
  private environment: Environment;
  validationResults: ValidationResult[] = [];
  preValidationResults: ValidationResult[] = [];
  validationEnvironments: ValidationEnvironment[] = [];
  pipelineReviewRequestSent: boolean = false;
  pipelineLockedMessageSent: boolean = false;
  stepperOrientation: Observable<StepperOrientation>;
  rcAction?: string;
  rcActionTime?: string;
  releaseDefinitionId?: string | null;
  hasPreValidationErrorsOrWarnings: boolean = false;
  hasPreValidationWarnings: boolean = false;
  hasPreValidationErrors: boolean = false;
  forceReviewRequested: boolean = false;
  currentReleaseRevision?: string;
  lastUnlockedReleaseRevision?: string;
  @ViewChild('myStepper') stepper!: MatStepper;
  @ViewChild('releaseInput') releaseInput!: any;

  // Subliminal YAML messaging - Matrix-style cascade
  subliminalMessages = [
    'YAML Pipelines...',
    'Have you tried YAML?',
    'YAML is the way',
    'Embrace the YAML',
    '🚀 YAML',
    'Classic is so 2019',
    'Join the YAML side',
    'YAML > Classic'
  ];
  showCascade = false;
  cascadeMessages: Array<{ text: string; left: number; delay: number; size: number }> = [];

  constructor(
    @Inject(AdoService) private readonly adoService: AdoService,
    @Inject(RcjitService) private readonly jitService: RcjitService,
    @Inject(FormBuilder) private readonly formBuilder: FormBuilder,
    @Inject(EnvironmentService) private readonly environmentService: EnvironmentService,
    @Inject(SlackService) private readonly slackService: SlackService,
    @Inject(MatDialog) private readonly dialog: MatDialog,
    @Inject(ActivatedRoute) private readonly route: ActivatedRoute,
    @Inject(UserProfileService) private readonly userProfileService: UserProfileService,
    @Inject(BreakpointObserver) private readonly breakpointObserver: BreakpointObserver,
    @Inject(DomSanitizer) private readonly sanitizer: DomSanitizer,
    public loadingService: LoadingService
  ) {
    this.releaseDefinitions = new Array<ReleaseDefinition>();
    this.filteredReleaseDefinitions = of(this.releaseDefinitions);
    this.firstFormGroup = this.formBuilder.group({
      releaseDefinition: ['', [Validators.required, this.releaseDefinitionValidator()]]
    });

    this.secondFormGroup = this.formBuilder.group({
      request: ['', false],
    });

    this.thirdFormGroup = this.formBuilder.group({
      request: ['', false],
    });

    this.environment = this.environmentService.getEnvironment();

    this.stepperOrientation = this.breakpointObserver
      .observe('(min-width: 800px)')
      .pipe(map(({ matches }) => (matches ? 'horizontal' : 'vertical')));
  }

  ngOnDestroy(): void {
    this._destroying$.next();
    this._destroying$.complete();
  }

  ngOnInit(): void {
    // Start the subliminal YAML messaging
    this.startSubliminalFlashing();

    // Subscribe to authentication state
    this.userProfileService.isAuthenticated()
      .pipe(takeUntil(this._destroying$))
      .subscribe(isAuthenticated => {
        this.isAuthenticated = isAuthenticated;

        if (isAuthenticated) {
          // Check if user has Platform.Admin role
          this.userProfileService.hasRole('Platform.Admin')
            .pipe(takeUntil(this._destroying$))
            .subscribe(isPlatformAdmin => {
              this.isPlatform = isPlatformAdmin;
            });

          // Load user profile and continue initialization
          this.LoadUserProfile()
            .pipe(
              tap((profile: AdoUserProfile) => {
                this.userProfile = profile;
              })
            )
            .subscribe(() => {
              this.LoadAccounts().add(() => {
                this.GetAvailableOrgs();
              });
            });
        }
      });

    this.releaseDefinitionId = this.route.snapshot.paramMap.get('id');
  }

  ngAfterViewInit() {
    if (this.releaseDefinitionId) return;
    setTimeout(() => {
      this.releaseInput.nativeElement.focus();
    }, 100);
  }

  _filter(val: string | ReleaseDefinition): Array<ReleaseDefinition> {
    const filterValue: string = typeof val === 'string' ? val.toLowerCase() : val.name.toLowerCase();

    return this.releaseDefinitions.filter((option) =>
      option.name.toLowerCase().includes(filterValue)
    );
  }

  displayRcName(rcDef?: ReleaseDefinition): string {
    return rcDef ? rcDef.name : '';
  }

  reload() {
    window.location.reload();
  }

  LoadUserProfile(): Observable<AdoUserProfile> {
    this.loadingService.show('userProfile');
    return this.adoService.GetUserProfile().pipe(tap(() => this.loadingService.hide('userProfile')));
  }

  GetAvailableOrgs(): void {
    this.jitService
      .getAvailableOrgs()
      .subscribe((orgs) => {
        this.availableAdoAccounts = this.availableAdoAccounts.filter(
          (item) =>
            orgs.findIndex(
              (org) => item.accountName.toLowerCase() === org.toLowerCase()
            ) >= 0
        );
      })
      .add(() => {
        this.availableAdoAccounts.forEach((account) => {
          account.projects = new Array<string>();
          this.adoService
            .GetProjects(account.accountName)
            .subscribe((projects) => {
              projects.value.forEach((project: any) => {
                account.projects.push(project.name);
              });
              this.loadingService.hide('projects');
            })
            .add(() => {
              if (this.defaultOrg?.accountName === account.accountName) {
                this.defaultOrg.projects = account.projects;
                this.SetDefaultProject();
                this.LoadReleaseDefinitions(this.defaultOrg.accountName, this.defaultProject!);
              }
            });
        });
        this.SetDefaultOrg();
      });
  }

  SetDefaultOrg(): void {
    this.defaultOrg = this.availableAdoAccounts.filter(x => x.accountName.toLowerCase() === this.environment.defaultOrg.toLowerCase())[0] ?? undefined;
  }

  SetDefaultProject(): void {
    const defaultProject = this.defaultOrg!.projects.filter(x => x.toLowerCase() === this.environment.defaultProject.toLowerCase())[0];
    this.defaultProject = defaultProject;
  }

  LoadAccounts(): Subscription {
    this.loadingService.show('accounts');
    return this.adoService
      .GetAccounts(this.userProfile.publicAlias)
      .subscribe((accounts: AdoResponse<AdoAccount>) => {
        this.availableAdoAccounts = accounts.value;
        this.loadingService.hide('accounts');
      });
  }

  LoadReleaseDefinitions(org: string, project: string): void {
    this.loadingService.show('releaseDefinitions');
    this.releaseDefinitions = new Array<ReleaseDefinition>();

    this.adoService.GetReleaseDefinitions(org, project)
      .subscribe((result: any) => {
        for (const def of result.value) {
          this.releaseDefinitions.push({ name: def.name, id: def.id });

          if (this.releaseDefinitionId && this.releaseDefinitionId === def.id.toString()) {
            this.firstFormGroup.controls['releaseDefinition'].setValue(def);
            this.getReleaseDefinitionChanges(def.id, org, project);
            return;
          }
        }

        this.releaseDefinitions.sort(this.compareNameSort);
        this.filteredReleaseDefinitions = this.firstFormGroup.valueChanges.pipe(
          startWith<string | any>(''),
          map((value) => typeof value === 'string' ? value : value.releaseDefinition),
          map((value) => value === '' ? this.releaseDefinitions : this._filter(value))
        );

        this.loadingService.hide('releaseDefinitions');
      });
  }

  compareNameSort(a: ReleaseDefinition, b: ReleaseDefinition): number {
    let nameOne = a.name.toLowerCase();
    let nameTwo = b.name.toLowerCase();
    let compareResult = 0;
    if (nameOne > nameTwo) compareResult = 1;
    if (nameOne < nameTwo) compareResult = -1;
    return compareResult;
  }

  releaseDefinitionValidator(): ValidatorFn {
    return (control: AbstractControl): { [key: string]: any } | null => {
      if (typeof control.value === 'string') {
        if (!this.releaseDefinitions.some(r => r.name === control.value)) {
          return { 'invalidReleaseDefinition': { value: control.value } }
        }
        control.setValue(this.releaseDefinitions.find(r => r.name === control.value))
      }
      return null
    }
  }

  unlockRelease(releaseId: number, org: string, project: string) {
    this.loadingService.show('unlock');
    this.preValidationResults = [];
    this.jitService
      .unlockRelease(org, project, releaseId)
      .subscribe((response) => {
        this.jitFinished = true;
        this.pipelineReviewRequestSent = false;
        this.pipelineLockedMessageSent = false;
        this.loadingService.hide('unlock');
        this.rcAction = 'unlocked';
        this.setRcActionTime();
        this.stepper.next();
        this.preValidate(response);
      });
  }

  lockRelease(releaseId: number, org: string, project: string) {
    this.loadingService.show('lock');
    this.jitService
      .lockRelease(org, project, releaseId)
      .subscribe(() => {
        this.jitFinished = true;
        this.pipelineReviewRequestSent = false;
        this.pipelineLockedMessageSent = false;
        this.loadingService.hide('lock');
        this.rcAction = 'locked';
        this.setRcActionTime();
        this.stepper.next();
      });
  }

  sendPipelineReviewRequest(org: string, project: string, releaseDefinition: ReleaseDefinition, forceReview: boolean) {
    this.jitFinished = false;
    if (forceReview) {
      this.forceReviewRequested = true;
      this.sendPipelineReview(org, project, releaseDefinition, forceReview);
      return;
    }
    this.validateRelease(releaseDefinition.id, org, project).subscribe((validationSuccessful: boolean) => {
      this.forceReviewRequested = false;
        if (validationSuccessful) {

          this.pipelineReviewRequestSent = false;
          this.pipelineLockedMessageSent = true;
          this.setRcActionTime();
          this.stepper.next();

        }
        else if(this.preValidationResults.length === 0 ) {
            this.sendPipelineReview(org, project, releaseDefinition, forceReview);
        }
    });
  }

  sendPipelineReview(org: string, project: string, releaseDefinition: ReleaseDefinition, forceReview: boolean) {
    this.slackService.sendPipelineReviewRequest(this.userProfile, org, project, releaseDefinition, forceReview).subscribe(
      response => {
        if (response === "ok") {
          this.pipelineReviewRequestSent = true;
          this.pipelineLockedMessageSent = false;
          this.setRcActionTime();
          this.stepper.next();
        } else {
          this.pipelineReviewRequestSent = false;
          this.pipelineLockedMessageSent = false;
          this.openErrorDialog(`Error sending Slack message: ${response}`);
        }
      });
  }

  setRcActionTime() {
    const currentDate = new Date();
    const formattedDate = `${currentDate.toLocaleTimeString([], { hour: 'numeric', minute: '2-digit', hour12: true })}`;
    this.rcActionTime = formattedDate;
  }

  preValidate(validationResults: ValidationResult[]) {
    if(validationResults?.length > 0) {
        this.addPreValidationResults(validationResults);
        this.validationEnvironments = this.groupValidationErrorsByEnvironment(this.preValidationResults);
    } else {
        this.validationEnvironments = [];
    }
    this.refreshValidationState();
  }

  groupValidationErrorsByEnvironment(validationResults: ValidationResult[]): ValidationEnvironment[] {
    const groupedEnvironments: Record<string, ValidationEnvironment> = {};
    let x = JSON.stringify(validationResults);
    console.log(x);
    validationResults.forEach(result => {
      result.ValidationResults.forEach(resultEntry => {
        const resultEnvironment = resultEntry.Environment;
        if (!groupedEnvironments[resultEnvironment]) {
          groupedEnvironments[resultEnvironment] = {
            EnvironmentName: resultEnvironment,
            ValidationResults: {},
          };
        }
        const taskName = resultEntry.Task;
        if (!groupedEnvironments[resultEnvironment].ValidationResults[taskName]) {
          groupedEnvironments[resultEnvironment].ValidationResults[taskName] = [];
        }

        groupedEnvironments[resultEnvironment].ValidationResults[taskName].push(resultEntry);
      });
    });
    return Object.values(groupedEnvironments);
  }

  clearPreValidateTable(): void {
    this.preValidationResults = [];
    this.hasPreValidationErrorsOrWarnings = false;
    this.hasPreValidationWarnings = false;
    this.hasPreValidationErrors = false;
  }

  refreshValidationState() {
    this.hasPreValidationErrors = this.preValidationResults?.some(result => result.Status === ValidationStatusEnum.Error);
    this.hasPreValidationWarnings = this.preValidationResults?.some(result => result.Status === ValidationStatusEnum.Warning);
    this.hasPreValidationErrorsOrWarnings = this.hasPreValidationErrors || this.hasPreValidationWarnings;
  }

  validateRelease(releaseId: number, org: string, project: string): Observable<boolean> {
    this.loadingService.show('validate');
    this.clearPreValidateTable();
    this.refreshValidationState();
    return this.jitService.validateRelease(org, project, releaseId).pipe(
      map((validationResults: ValidationResult[]) => {
        if (validationResults && validationResults.length > 0) {
          this.jitFinished = true;
          this.preValidate(validationResults);
          this.addValidationResults(validationResults);
          this.loadingService.hide('validate');
          this.refreshValidationState();
          return false;
        } else {
          this.refreshValidationState();
          this.loadingService.hide('validate');
          return true;
        }
      }),
      catchError((error: HttpErrorResponse) => {
        this.loadingService.hide('validate');
        this.openErrorDialog(`Error while validating pipeline: ${error.status} ${error.statusText}`);
        return throwError(() => new Error(error.message));
      })
    );
  }

  addValidationResults(validationResults: ValidationResult[]) {
    this.validationResults = [];
    this.validationResults.push(...validationResults.filter(result => result.Type === ValidationResultType.Validate));
  }

  addPreValidationResults(preValidationResults: ValidationResult[]) {
    this.preValidationResults = [];
    this.preValidationResults.push(...preValidationResults.filter(result => result.Type === ValidationResultType.PreValidate));
  }

  openErrorDialog(errorMessage: string): void {
    this.dialog.open(ErrorDialogComponent, {
      data: { message: errorMessage }
    });
  }

  isSecondOrThirdStep(): boolean {
    return this.stepper?.selectedIndex === 1 || this.stepper?.selectedIndex === 2;
  }

  getSelectedIndex(): number {
    return this.jitFinished && this.hasPreValidationErrorsOrWarnings ? 1 : 0;
  }

  getReleaseDefinitionChanges(releaseId: number, org: string, project: string) {
    this.loadingService.show('changes');
    this.jitService.getReleaseChanges(org, project, releaseId)
    .subscribe((response: HttpResponse<ReleaseDefinitionChangesResult>) => {
      if (response.body) {
        this.lastUnlockedReleaseRevision = JSON.stringify(response.body.lastUnlockedReleaseDefinition);
        this.currentReleaseRevision = JSON.stringify(response.body.currentReleaseDefinition);
        this.stepper.next();
        this.loadingService.hide('changes');
      }
    });
  }

  sanitizeHtml(html: string): SafeHtml {
    return this.sanitizer.sanitize(SecurityContext.HTML, html) ?? '';
  }

  private startSubliminalFlashing(): void {
    const triggerCascade = () => {
      // Generate 12-20 cascading messages at random positions
      const messageCount = 12 + Math.floor(Math.random() * 8);
      this.cascadeMessages = [];

      for (let i = 0; i < messageCount; i++) {
        this.cascadeMessages.push({
          text: this.subliminalMessages[Math.floor(Math.random() * this.subliminalMessages.length)],
          left: Math.random() * 90, // 0-90% from left
          delay: Math.random() * 2000, // staggered start over 2 seconds
          size: 18 + Math.floor(Math.random() * 16) // 18-34px font size
        });
      }

      this.showCascade = true;

      // Hide after all animations complete (3s animation + 2s max delay + buffer)
      setTimeout(() => {
        this.showCascade = false;
        this.cascadeMessages = [];
      }, 5500);

      // Schedule next cascade
      const nextCascade = 8000 + Math.random() * 12000; // 8-20 seconds
      setTimeout(triggerCascade, nextCascade);
    };

    // First cascade after 3-8 seconds
    setTimeout(triggerCascade, 3000 + Math.random() * 5000);
  }
}
